import { z } from 'zod';
import { AI_PROCESSING_STATUSES, AI_SENTIMENTS, AUDIO_CHANNEL_MODES, CALL_STATUSES, COMMUNICATION_DIRECTIONS, RESOLVED_AUDIO_CHANNEL_MODES, TELEPHONY_LOG_PAGE_SIZE, TELEPHONY_MAX_ACTION_ITEMS, TELEPHONY_MAX_TOPICS, TELEPHONY_NOTES_MAX_LENGTH, TELEPHONY_PROVIDERS, TELEPHONY_SUMMARY_MAX_LENGTH, TRANSCRIPT_SPEAKER_PATTERN, } from '../constants/telephony.js';
import { idSchema, isoDateTimeSchema } from './common.js';
import { partnerRiskStatusSchema } from './enums.js';
import { paginationMetaSchema } from './pagination.js';
import { e164PhoneSchema } from './whatsapp.js';
export const telephonyProviderSchema = z.enum(TELEPHONY_PROVIDERS);
export const audioChannelModeSchema = z.enum(AUDIO_CHANNEL_MODES);
export const resolvedAudioChannelModeSchema = z.enum(RESOLVED_AUDIO_CHANNEL_MODES);
export const communicationDirectionSchema = z.enum(COMMUNICATION_DIRECTIONS);
export const callStatusSchema = z.enum(CALL_STATUSES);
export const aiProcessingStatusSchema = z.enum(AI_PROCESSING_STATUSES);
export const aiSentimentSchema = z.enum(AI_SENTIMENTS);
// ---------------------------------------------------------------------------
// Transkript und KI-Ergebnis
// ---------------------------------------------------------------------------
/**
 * Ein Sprecherabschnitt des Dialogs. `AGENT`/`CUSTOMER` entstehen bei der
 * Stereo-Kanaltrennung, `SPEAKER_00`/`SPEAKER_01` bei der Diarisierung.
 */
export const transcriptSegmentSchema = z.object({
    speaker: z.string().regex(TRANSCRIPT_SPEAKER_PATTERN, 'Unbekannte Sprecherkennung.'),
    text: z.string(),
    start: z.number().min(0),
    end: z.number().min(0),
});
export const aiActionItemSchema = z.object({
    id: z.string().min(1).max(64),
    task: z.string().trim().min(1).max(500),
    completed: z.boolean(),
});
// ---------------------------------------------------------------------------
// Benutzerbezogene Telefonie-Konfiguration
// ---------------------------------------------------------------------------
/** Geheimnisse werden nie zurückgegeben; gemeldet wird nur, ob ein Wert hinterlegt ist. */
export const telephonySecretStateSchema = z.object({
    isSet: z.boolean(),
    hint: z.string().nullable(),
});
export const userTelephonyConfigSchema = z.object({
    userId: idSchema,
    userName: z.string(),
    userEmail: z.string().email(),
    isEnabled: z.boolean(),
    provider: telephonyProviderSchema,
    providerUserId: z.string().nullable(),
    internalExtension: z.string().nullable(),
    /// Vollständige Direktwahl in E.164; ordnet eingehende Rufe ohne Agent-Kennung zu.
    directDialNumber: z.string().nullable(),
    /// Rufnummer, die bei ausgehenden Gesprächen übermittelt wird.
    outboundCallerId: z.string().nullable(),
    autoOpenScreenPop: z.boolean(),
    clickToCallEnabled: z.boolean(),
    apiKey: telephonySecretStateSchema,
    updatedAt: isoDateTimeSchema.nullable(),
});
export const userTelephonyConfigUpdateSchema = z.object({
    isEnabled: z.boolean(),
    provider: telephonyProviderSchema,
    providerUserId: z.string().trim().min(1).max(120).nullable(),
    internalExtension: z
        .string()
        .trim()
        .regex(/^\d{1,10}$/u, 'Die Durchwahl besteht aus Ziffern.')
        .nullable(),
    directDialNumber: e164PhoneSchema.nullable(),
    outboundCallerId: e164PhoneSchema.nullable(),
    autoOpenScreenPop: z.boolean(),
    clickToCallEnabled: z.boolean(),
    /** `undefined` bewahrt den gespeicherten Schlüssel, `null` entfernt ihn. */
    apiKey: z.string().trim().min(8).max(512).nullish(),
});
export const userTelephonyConfigListSchema = z.object({
    data: z.array(userTelephonyConfigSchema),
});
// ---------------------------------------------------------------------------
// Installationsweite Anbieterkonfiguration
// ---------------------------------------------------------------------------
/**
 * Ringover signiert sämtliche Webhooks mit genau einem Secret je Integration.
 * Die Ablage ist deshalb installationsweit; der Mandant ergibt sich unverändert
 * erst aus der Agent- beziehungsweise Rufnummern-Auflösung.
 */
export const globalTelephonySettingsSchema = z.object({
    ringoverWebhookSecret: telephonySecretStateSchema,
    ringoverMasterApiKey: telephonySecretStateSchema,
    /** Vollständige Adresse, die im Ringover-Dashboard hinterlegt wird. */
    webhookUrl: z.string().url(),
    updatedAt: isoDateTimeSchema.nullable(),
});
export const globalTelephonySettingsUpdateSchema = z.object({
    ringoverWebhookSecret: z.string().trim().min(16).max(512).nullish(),
    ringoverMasterApiKey: z.string().trim().min(8).max(512).nullish(),
});
/** Ergebnis des Live-Aufrufs gegen die Anbieter-API. */
export const telephonyConnectionTestSchema = z.object({
    success: z.boolean(),
    agentName: z.string().nullable(),
    message: z.string(),
    checkedAt: isoDateTimeSchema,
});
// ---------------------------------------------------------------------------
// Systemweite KI- und STT-Konfiguration
// ---------------------------------------------------------------------------
export const aiTelephonySettingsSchema = z.object({
    isEnabled: z.boolean(),
    audioProcessingMode: audioChannelModeSchema,
    whisperApiUrl: z.string().url(),
    whisperApiKey: telephonySecretStateSchema,
    ollamaApiUrl: z.string().url(),
    ollamaModel: z.string(),
    systemPromptTemplate: z.string(),
    updatedAt: isoDateTimeSchema,
});
export const aiTelephonySettingsUpdateSchema = z.object({
    isEnabled: z.boolean(),
    audioProcessingMode: audioChannelModeSchema,
    whisperApiUrl: z.string().trim().url().max(500),
    /** Leer lassen bewahrt den gespeicherten Schlüssel; `null` entfernt ihn. */
    whisperApiKey: z.string().trim().min(8).max(512).nullable().optional(),
    ollamaApiUrl: z.string().trim().url().max(500),
    ollamaModel: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[\w./:-]+$/u, 'Der Modellname enthält unzulässige Zeichen.'),
    systemPromptTemplate: z.string().trim().min(40).max(8000),
});
/** Schrittweiser Bericht des Pipeline-Tests im Administrationsbereich. */
export const telephonyPipelineStepSchema = z.object({
    step: z.enum(['ANALYSIS', 'TRANSCRIPTION', 'SUMMARY']),
    isSuccess: z.boolean(),
    durationMs: z.number().int().min(0),
    message: z.string(),
});
export const telephonyPipelineTestSchema = z.object({
    isSuccess: z.boolean(),
    detectedAudioFormat: z.string(),
    usedProcessingMode: resolvedAudioChannelModeSchema.nullable(),
    steps: z.array(telephonyPipelineStepSchema),
    transcriptSegments: z.array(transcriptSegmentSchema),
    transcriptText: z.string(),
    summary: z.string().nullable(),
    sentiment: aiSentimentSchema.nullable(),
    actionItems: z.array(aiActionItemSchema),
    topics: z.array(z.string()),
    checkedAt: isoDateTimeSchema,
});
// ---------------------------------------------------------------------------
// Kommunikationsprotokoll
// ---------------------------------------------------------------------------
export const communicationLogSchema = z.object({
    id: idSchema,
    callId: z.string(),
    provider: telephonyProviderSchema,
    userId: idSchema,
    userName: z.string(),
    partnerId: idSchema.nullable(),
    partnerName: z.string().nullable(),
    partnerNumber: z.string().nullable(),
    contactId: idSchema.nullable(),
    contactName: z.string().nullable(),
    salesTaskId: idSchema.nullable(),
    salesRecommendationId: idSchema.nullable(),
    direction: communicationDirectionSchema,
    callerNumber: z.string(),
    calleeNumber: z.string(),
    status: callStatusSchema,
    durationSeconds: z.number().int().min(0),
    startedAt: isoDateTimeSchema,
    endedAt: isoDateTimeSchema.nullable(),
    hasRecording: z.boolean(),
    manualNotes: z.string().nullable(),
    tags: z.array(z.string()),
    createdAt: isoDateTimeSchema,
    /**
     * KI-Felder werden ohne `TELEPHONY:VIEW_AI_SUMMARY` serverseitig entfernt;
     * `isAiVisible` sagt der Oberfläche, ob sie den Bereich überhaupt anzeigt.
     */
    isAiVisible: z.boolean(),
    aiProcessingStatus: aiProcessingStatusSchema.nullable(),
    detectedAudioFormat: z.string().nullable(),
    usedProcessingMode: resolvedAudioChannelModeSchema.nullable(),
    transcriptSegments: z.array(transcriptSegmentSchema),
    transcriptText: z.string().nullable(),
    aiSummary: z.string().nullable(),
    aiSummaryOriginal: z.string().nullable(),
    isAiSummaryEdited: z.boolean(),
    aiSummaryEditedByName: z.string().nullable(),
    aiSummaryEditedAt: isoDateTimeSchema.nullable(),
    aiSentiment: aiSentimentSchema.nullable(),
    aiActionItems: z.array(aiActionItemSchema),
    aiTopics: z.array(z.string()),
    aiErrorMessage: z.string().nullable(),
});
export const communicationLogScopes = ['MINE', 'ALL'];
export const communicationLogScopeSchema = z.enum(communicationLogScopes);
export const communicationLogListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(TELEPHONY_LOG_PAGE_SIZE),
    scope: communicationLogScopeSchema.default('MINE'),
    partnerId: idSchema.optional(),
    direction: communicationDirectionSchema.optional(),
    status: callStatusSchema.optional(),
    q: z.string().trim().min(1).max(200).optional(),
});
export const communicationLogListResponseSchema = z.object({
    data: z.array(communicationLogSchema),
    meta: paginationMetaSchema,
});
export const communicationLogNotesUpdateSchema = z.object({
    manualNotes: z.string().trim().max(TELEPHONY_NOTES_MAX_LENGTH).nullable(),
    tags: z.array(z.string().trim().min(1).max(60)).max(20),
});
export const communicationLogAiSummaryUpdateSchema = z.object({
    aiSummary: z.string().trim().min(1).max(TELEPHONY_SUMMARY_MAX_LENGTH),
    aiSentiment: aiSentimentSchema.optional(),
    aiActionItems: z.array(aiActionItemSchema).max(TELEPHONY_MAX_ACTION_ITEMS).optional(),
    aiTopics: z.array(z.string().trim().min(1).max(60)).max(TELEPHONY_MAX_TOPICS).optional(),
});
export const communicationLogActionItemsUpdateSchema = z.object({
    aiActionItems: z.array(aiActionItemSchema).max(TELEPHONY_MAX_ACTION_ITEMS),
});
// ---------------------------------------------------------------------------
// Screen-Pop und Click-to-Call
// ---------------------------------------------------------------------------
export const clickToCallSchema = z
    .object({
    phoneNumber: e164PhoneSchema,
    /** Der Zweck wird bei verknüpften Vertriebsaufgaben serverseitig stets als SALES behandelt. */
    purpose: z.enum(['GENERAL', 'SALES', 'SERVICE', 'CALLBACK']).optional(),
    partnerId: idSchema.optional(),
    contactId: idSchema.optional(),
    /** Verknüpft den Ruf mit einem bereits planbaren Vertriebsprozess. */
    salesTaskId: idSchema.optional(),
    salesRecommendationId: idSchema.optional(),
    /** Lokalisierte Aufgabenbezeichnung für einen direkten Ruf ohne bestehende Aufgabe. */
    followUpTask: z
        .object({
        title: z.string().trim().min(1).max(240),
        reason: z.string().trim().min(1).max(10_000),
    })
        .optional(),
})
    .superRefine((value, context) => {
    if (value.followUpTask !== undefined && value.salesTaskId !== undefined) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['followUpTask'],
            message: 'TELEPHONY_FOLLOW_UP_TASK_CONFLICT',
        });
    }
    if (value.followUpTask !== undefined && value.partnerId === undefined) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['partnerId'],
            message: 'TELEPHONY_FOLLOW_UP_PARTNER_REQUIRED',
        });
    }
    if (value.salesRecommendationId !== undefined &&
        value.salesTaskId === undefined &&
        value.followUpTask === undefined) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['followUpTask'],
            message: 'TELEPHONY_RECOMMENDATION_TASK_REQUIRED',
        });
    }
});
export const clickToCallResultSchema = z.object({
    callId: z.string(),
    logId: idSchema,
    salesTaskId: idSchema.nullable(),
    salesRecommendationId: idSchema.nullable(),
    message: z.string(),
});
/** Beendeter CTI-Ruf, dessen strukturierte Kontaktdokumentation noch aussteht. */
export const pendingSalesCallOutcomeSchema = z.object({
    communicationLogId: idSchema,
    callId: z.string(),
    salesTaskId: idSchema,
    salesRecommendationId: idSchema.nullable(),
    partnerId: idSchema,
    partnerName: z.string(),
    partnerNumber: z.string(),
    direction: communicationDirectionSchema,
    status: callStatusSchema,
    durationSeconds: z.number().int().min(0),
    startedAt: isoDateTimeSchema,
    endedAt: isoDateTimeSchema,
});
export const pendingSalesCallOutcomeListSchema = z.object({
    data: z.array(pendingSalesCallOutcomeSchema),
});
/** Auflösung einer eingehenden Rufnummer für die Kontaktkarte (Screen-Pop). */
export const screenPopQuerySchema = z.object({
    phoneNumber: z.string().trim().min(3).max(32),
});
export const screenPopSchema = z.object({
    phoneNumber: z.string(),
    partnerId: idSchema.nullable(),
    partnerName: z.string().nullable(),
    partnerNumber: z.string().nullable(),
    partnerUrl: z.string().nullable(),
    contactId: idSchema.nullable(),
    contactName: z.string().nullable(),
    openDocumentCount: z.number().int().min(0),
    lastCallAt: isoDateTimeSchema.nullable(),
    /** Risikoeinstufung des Anrufers; die Einblendung alarmiert bei Sperre sofort. */
    riskStatus: partnerRiskStatusSchema,
    isBlocked: z.boolean(),
    blockReason: z.string().nullable(),
});
/** Aktive Rufe des angemeldeten Benutzers; Grundlage des Screen-Pop-Fensters. */
export const activeCallListSchema = z.object({
    data: z.array(communicationLogSchema),
});
// ---------------------------------------------------------------------------
// Anbieter-Webhook (anbieterneutral normalisiert)
// ---------------------------------------------------------------------------
export const telephonyWebhookSchema = z.object({
    event: z.enum(['RINGING', 'ANSWERED', 'HANGUP', 'MISSED', 'VOICEMAIL', 'FAILED', 'RECORDING']),
    callId: z.string().trim().min(1).max(200),
    provider: telephonyProviderSchema.default('RINGOVER'),
    direction: communicationDirectionSchema,
    fromNumber: z.string().trim().min(3).max(32),
    toNumber: z.string().trim().min(3).max(32),
    /** Agent-Kennung oder Durchwahl des Anbieters; ordnet den Ruf einem Benutzer zu. */
    agentId: z.string().trim().min(1).max(120).optional(),
    agentExtension: z.string().trim().min(1).max(10).optional(),
    startedAt: isoDateTimeSchema.optional(),
    endedAt: isoDateTimeSchema.optional(),
    durationSeconds: z.coerce.number().int().min(0).max(86_400).optional(),
    recordingUrl: z.string().trim().url().max(1000).optional(),
});
//# sourceMappingURL=telephony.js.map